import React from 'react';
import { addDays, differenceInDays, endOfDay, format, startOfDay } from 'date-fns';
import { RadioWithLabel } from 'components';
import { Calendar } from 'react-date-range';

interface ExpiryDateSectionProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  showCustomPicker: boolean;
  onCustomPickerChange: (show: boolean) => void;
}

const ExpiryDateSection: React.FC<ExpiryDateSectionProps> = ({
  selectedDate,
  onDateChange,
  showCustomPicker,
  onCustomPickerChange
}) => {
  const today = startOfDay(new Date());

  const expiryOptions = [
    { days: 15, label: '15 days' },
    { days: 30, label: '30 days' },
    { days: 45, label: '45 days' },
    { days: 60, label: '60 days' },
    { days: 90, label: '90 days' },
    { days: -1, label: 'Custom' }
  ];

  const getSelectedDays = (date: Date): number => {
    const todayStart = startOfDay(new Date());
    const expiryEnd = endOfDay(date);
    const diffDays = differenceInDays(expiryEnd, todayStart);
    return expiryOptions.find(opt => opt.days === diffDays)?.days ?? -1;
  };

  const formatExpiryDate = (days: number) => {
    if (days === -1) {
      if (getSelectedDays(selectedDate) === days) {
        return `( On ${format(selectedDate, 'MMM d, yyyy')} )`;
      }
      return '';
    }
    const expiryDate = endOfDay(addDays(today, days));
    return `( On ${format(expiryDate, 'MMM d, yyyy')} )`;
  };

  const formatLabel = (option: { days: number; label: string }) => {
    if (option.days === -1) {
      return `${option.label} ${formatExpiryDate(option.days)}`;
    }
    return `In ${option.label} ${formatExpiryDate(option.days)}`;
  };

  const handleOptionClick = (days: number) => {
    if (days === -1) {
      onCustomPickerChange(true);
    } else {
      onCustomPickerChange(false);
      onDateChange(endOfDay(addDays(today, days)));
    }
  };

  const handleDateSelect = value => {
    onDateChange(new Date(value));
    onCustomPickerChange(false);
  };

  if (showCustomPicker) {
    return (
      <div className="flex flex-col gap-4">
        <Calendar
          date={selectedDate}
          onChange={value => {
            handleDateSelect(value);
          }}
          minDate={new Date()}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {expiryOptions.map(option => (
        <div
          className={`border-b border-secondaryBtn pb-4 ${
            option.days === expiryOptions[expiryOptions.length - 1].days ? 'border-b-0' : ''
          }`}
          key={option.days}>
          <RadioWithLabel
            isSelected={getSelectedDays(selectedDate) === option.days}
            label={formatLabel(option)}
            onClick={() => handleOptionClick(option.days)}
            labelStyle={{ display: 'flex', gap: '8px', flex: 1 }}
            radioPosition="right"
          />
        </div>
      ))}
    </div>
  );
};

export default ExpiryDateSection;
